import { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { mq } from '~/styles/grid';
import isBreakpointInMediaQuery from '~/helpers/isBreakpointInMediaQuery';
import { currentBreakpoint } from '~/helpers/getBreakpoint';

const handleResize = (useMq, theme, contextKey) => {
  const breakpoint = currentBreakpoint(window.innerWidth);

  if (useMq) {
    const mediaQuery = mq[useMq];
    return isBreakpointInMediaQuery(breakpoint, mediaQuery);
  }

  if (!contextKey || !theme[contextKey]) {
    console.error(`Context key "${contextKey}" does not exist in the theme.`);
    return false;
  }

  const { mobileSwitch } = theme[contextKey];

  if (!mobileSwitch) {
    console.error(
      `Mobile switch is not defined for context key "${contextKey}".`,
    );
    return false;
  }

  return isBreakpointInMediaQuery(breakpoint, mobileSwitch);
};

/**
 * Custom hook to determine if the viewport matches a mobile breakpoint.
 * @param {Object} [props] - The parameters object.
 * @param {string | undefined} [props.contextKey] - The key to access the specific context inside the theme (e.g., 'table', 'header').
 * @param {string | undefined} [props.useMq] - The key to access the specific media query from the mq object.
 * @returns {boolean} - True if the current breakpoint matches the mobile view, false otherwise.
 */
const useIsMobile = (props) => {
  const contextKey = props?.contextKey;
  const useMq = props?.useMq ?? 'xs';

  const theme = useTheme();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') {
      // SSR / no window: pick a safe default
      return false;
    }

    return handleResize(useMq, theme, contextKey);
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMobile(handleResize(useMq, theme, contextKey));
    const onResize = () => {
      setIsMobile(handleResize(useMq, theme, contextKey));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  return isMobile;
};

export default useIsMobile;
